import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { IRequestCallback } from '@df/api/request/request.service';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { PRODUCT_VIEW_MODEL_CHANGE_EVENT_NAME } from '@df/catalog-ui/product/view/product-view.component.def';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { DfEventService } from '@df/core/service/df-event';
import { STATUS_SUCCESS, Status } from '@df/core/status';
import { Braintree } from '@df/module-braintree/braintree.namespace';
import { ExponeaServiceDef } from '@df/module-exponea/exponea.service.def';
import { Exponea } from '@df/module-exponea/exponea.types';
import { GtmService } from '@df/module-gtm/gtm.service';
import { NDfPrivacy } from '@df/module-privacy/df-module-privacy.interfaces';
import { PrivacyService } from '@df/module-privacy/privacy.service';
import { ProcessorService } from '@df/ng/processor.service';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { Newsletter } from '@df/session/newsletter/newsletter.interfaces';
import { NOTICES } from '@df/ui/api/notice/notice.const';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import compact from 'lodash-es/compact';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import isArray from 'lodash-es/isArray';
import map from 'lodash-es/map';
import { Subscription } from 'rxjs';
import { ProductDecorator } from './../../catalog/model/product/product.decorator';
import { CheckoutFormComponent } from './../../checkout/checkout-form.component';

declare module '@df/session/api/basket/basket' {
    interface IBasketData {
        cart_hash?: string;
    }
}

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            exponea?: string;
        }
    }
}

declare module '@df/module-exponea/exponea.types' {
    export namespace Exponea {
        export interface IProductEventProperties extends IExponeaLocalProductData, IExponeaLocalGeneralData {}

        export interface IViewCategoryEventProperties extends IExponeaLocalGeneralData {
            listed_products?: string[];
        }

        export interface IPurchaseEventProperties extends IExponeaLocalGeneralData, IExponeaPurchaseSource {
            voucher_code?: string;
            voucher_value?: number;
            voucher_percentage?: number;
            shipping_method?: string;
            shipping_cost?: number;
            location?: string;
            click_collect_store_name?: string;
            click_collect_store_id?: string;
            gift?: boolean;
            delivery_address?: string;
            delivery_postcode?: string;
            billing_address?: string;
            billing_postcode?: string;
            billing_city?: string;
            delivery_city?: string;
            payment_method?: string;
            delivery_date?: number;

            variant_list?: (ICartProductListItem | undefined)[];
            variant_ids?: (string | undefined)[];
        }

        export interface IPurchaseItemEventProperties extends IExponeaPurchaseSource {}

        export interface INewsletterConsent extends IExponeaLocalGeneralData {
            store_id?: number;
            placement?: 'public_api' | string;
            capture?: 'Magento';
            optout_date?: string;
        }

        export interface ICustomerIdentify {
            magento_customer_id?: string;
            watchdog_email?: string;
        }

        export interface ICheckoutStepChangeEventProperties extends IExponeaLocalGeneralData {
            variant_list?: (ICartProductListItem | undefined)[];
            variant_ids?: (string | undefined)[];
        }

        export interface ICustomerUpdate {
            cookies_accepted?: 'yes' | 'no';
        }

        export interface IStockNotificationData {
            email: string;
            newsletter?: boolean;
        }
    }
}

export interface IExponeaPurchaseSource {
    purchase_source_type?: 'online';
    purchase_source_name?: string;
}

export interface IExponeaLocalProductData {
    product_id: string;
    variant_id?: string;
    category_id?: string;
    category_1?: string;
    category_2?: string;
    category_3?: string;
    department?: string;
    product_type_filter?: string;
    style?: string;
    brand?: string;
    categories_path: string;
    categories_ids?: string[];
    price: number;
    original_price?: number;
    markdown_percentage?: number;
    markdown_value?: number;
    product_group?: string;
    sub_group?: string;
    typenumber?: string | number;
    groupnumber?: string | number;
    page_type?: string;
    sale_category?: boolean;
}

export interface IExponeaLocalBasketData {}

export interface IExponeaLocalGeneralData {
    local_currency?: string;
    language?: string;
    domain?: string;
}

export interface IExponeaProductAndVariantLists {
    product_list: (Exponea.ICartProductListItem | undefined)[];
    product_ids: (string | undefined)[];
    variant_list?: (Exponea.ICartProductListItem | undefined)[];
    variant_ids?: (string | undefined)[];
}

@Injectable({
    providedIn: 'root'
})
export class ExponeaService extends ExponeaServiceDef {
    protected sessionStorageService = inject(SessionStorageService);
    protected privacyService = inject(PrivacyService);
    protected dfEventService = inject(DfEventService);
    protected readonly isPlatformBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    protected window = inject(DOCUMENT).defaultView as Window & typeof globalThis;
    protected document = inject(DOCUMENT);
    protected gtmService = inject(GtmService);

    expressPaymentMethods = [Braintree.Methods.ApplePay, Braintree.Methods.GooglePay] as string[];

    privacyServiceSubscription?: Subscription;

    override track(event: Exponea.IEvent): void {
        if (this.privacyService.isAllowed(NDfPrivacy.AREA.advertising) || event.name === Exponea.Events.NewsletterConsent) {
            super.track(event);
        }
    }

    /**
     * Load script and initialize Exponea on success
     */
    protected override init(): void {
        if (typeof window === 'undefined') {
            return;
        }
        if (!window.exponea) {
            window.exponea = { _: [] } as any as Exponea.IExponea;
            each(Exponea.KEYS, value => {
                window.exponea[value] = this.getPushFunction(value);
            });
        }
        this.setListeners();
        // Need to inject it here because otherwise its not injected yet, even if placed in constructor
        const privacyService = ProcessorService.injector.get(PrivacyService);
        this.privacyServiceSubscription = privacyService.subscribeToPrivacyUpdate(() => {
            // Probably the only way of detecting if ontrust banner was closed, feel free to find better idea.
            // We cannot load exponea before onetrust cookie banner choices are made,
            // because load sets session cookie that will not be set without cookie consent
            // Related tasks: OBR-7814 OBR-7806
            if ([undefined, 'hidden'].includes(document.getElementById('onetrust-banner-sdk')?.style.visibility)) {
                this.load(() => {
                    this.initialize();
                });
                this.privacyServiceSubscription?.unsubscribe();
            }
        });
    }

    /**
     * add model view event so we can track configurable product selection change and use child model as data source
     * (meta only provides main/parent model for products and we need children SKU here.)
     */
    protected override setListeners() {
        super.setListeners();

        this.subscriptions.add(
            this.app.events.on(PRODUCT_VIEW_MODEL_CHANGE_EVENT_NAME).subscribe((product: ProductDecorator<IProductDataV3>) => {
                this.viewProductEvent(product);
            })
        );

        if (this.isPlatformBrowser) {
            this.dfEventService.success(event => {
                if (event.id === 'dfPrivacyChange') {
                    const value = event.data.settings?.['personalisation'] ? 'yes' : 'no';

                    if (window.exponea) {
                        window.exponea.update({ cookies_accepted: value });
                    }
                }
            });
        }

        // kill newsletter subscription; pass to further processing
        this.subscriptions.add(
            this.app.event.before(event => {
                switch (event.id) {
                    case 'nexusCustomerLogin':
                    case 'nexusCheckoutLogin':
                    case 'nexusCustomerRegister':
                        const data = event.data;
                        if ('newsletter' in data) {
                            event['newsletterBeforeClean'] = data.newsletter;
                            event['placement'] = data['placement'];
                            delete data.newsletter;
                            delete data['placement'];
                        }
                        break;
                }
            })
        );
        // read passed param and call exponea directly
        this.subscriptions.add(
            this.app.event.success(event => {
                switch (event.id) {
                    case 'nexusCustomerLogin':
                    case 'nexusCheckoutLogin':
                    case 'nexusCustomerRegister':
                        if ('newsletterBeforeClean' in event && 'placement' in event && event.newsletterBeforeClean !== undefined) {
                            this.onLoginOrRegisterRequest(!!event.newsletterBeforeClean, event.placement as string | undefined);
                        }
                        break;
                }
            })
        );

        // replace form subscription with another request
        this.subscriptions.add(
            this.app.events.on<Newsletter.ISubscriptionEvent>(Newsletter.subscriptionRequest).subscribe(event => {
                event.callback = data =>
                    new Promise((resolve, reject) => {
                        this.checkConsent(undefined, data.email, result => {
                            if (!result?.consent) {
                                resolve({
                                    success: STATUS_SUCCESS
                                });
                            } else {
                                reject({
                                    error: NOTICES['SUBSCRIBED']
                                });
                            }
                        });
                    }) as Promise<any>;
            })
        );
    }

    /**
     * overwrite meta product view event trigger so it is not fired twice.
     */
    protected override onMetaChange() {
        const path = this.meta.getMetaProperty('canonical') || this.meta.getMetaProperty('url') || this.window.location.pathname;

        if (this.lastPath !== path) {
            this.lastPath = path;
            const model = this.meta.model;

            if (model && model.type === 'category') {
                this.viewCategoryEvent(model as Category<ICategoryDataV3>);
            }
        }
    }

    protected override getProductEventProperties(
        product: ProductDecorator<IProductDataV2>
    ): Exponea.IViewProductEventProperties | undefined {
        const coreEventProperties = super.getProductEventProperties(product);
        if (coreEventProperties) {
            return {
                ...coreEventProperties,
                ...this.getLocalProductEventProperties(product),
                ...this.getLocalGeneralProperties(),
                page_type: this.getPageType()
            };
        }
        return undefined;
    }

    protected override getBasketItemEventProperties(
        action: Exponea.ItemsCartUpdateAction,
        basketItem: BasketItem
    ): Exponea.IBasketItemCartUpdateEventProperties | undefined {
        const coreEventProperties = super.getBasketItemEventProperties(action, basketItem);
        if (coreEventProperties) {
            const product = basketItem.product as ProductDecorator<IProductDataV3>;
            if (product) {
                return {
                    ...coreEventProperties,
                    ...this.getLocalProductEventProperties(product),
                    ...this.getLocalBasketEventProperties(),
                    ...this.getLocalGeneralProperties()
                };
            }
            return coreEventProperties;
        }
        return undefined;
    }

    protected getLocalBasketEventProperties(): IExponeaLocalBasketData {
        return {
            total_price: (this.basket.data.totals && this.basket.data.totals.grand_total * this.getPriceGBPRate()) || 0,
            total_price_without_tax:
                Math.round((this.basket.data.totals.grand_total - (this.basket.data.totals.tax || 0)) * this.getPriceGBPRate() * 100) /
                    100 || 0,
            quote_id: this.basket.data.quote_id,
            cart_hash: this.basket.data.cart_hash,
            ...this.getProductAndVariantLists()
        };
    }

    protected getLocalProductEventProperties(product: ProductDecorator<IProductDataV2>): IExponeaLocalProductData {
        const productData: IExponeaLocalProductData = {
            categories_ids: product.data.category_ids && map(product.data.category_ids, categoryId => categoryId.toString()),
            product_id: product.parent?.data?.sku || product.id.toString(),
            categories_path: this.getProductCategoriesPath(product),
            price: Math.round((product.data.price || 0) * this.getPriceGBPRate() * 100) / 100,
            original_price: Math.round((product.data.org_price || product.data.price || 0) * this.getPriceGBPRate() * 100) / 100
        };
        if (product.parent?.data?.sku !== product.data.sku) {
            productData.variant_id = product.data.sku;
        }
        const department = optionLabelPipe(product.data.department, 'department');
        if (typeof department === 'string') {
            productData.category_1 = department;
            productData.department = department;
        }
        const productTypeFilter = optionLabelPipe(product.data.product_type_filter, 'product_type_filter');
        if (typeof productTypeFilter === 'string') {
            productData.category_2 = productData.product_type_filter = productTypeFilter;
        }
        const style = optionLabelPipe(product.data.style, 'style');
        if (typeof style === 'string') {
            productData.category_3 = productData.style = style;
        }
        const brand = optionLabelPipe(product.data.brand, 'brand');
        if (typeof brand === 'string') {
            productData.brand = brand;
        }
        const productGroup = product.data.productgroup;
        if (productGroup) {
            productData.product_group = productGroup;
        }
        const categoryPath = product.data.category_path;
        if (categoryPath) {
            productData.categories_path = categoryPath;
        }
        const subGroup = product.data.subgroup;
        if (subGroup) {
            productData.sub_group = subGroup;
        }

        const typenumber = product.data.typenumber;
        if (typenumber) {
            productData.typenumber = typenumber;
        }

        const groupnumber = product.data.groupnumber;
        if (groupnumber) {
            productData.groupnumber = groupnumber;
        }

        productData.category_id = this.getProductCategoryIdFromMeta();
        // discount percentage
        productData.markdown_percentage = Math.round((1 - productData.price / (productData.original_price || productData.price)) * 100);
        productData.markdown_value = (productData.original_price || productData.price) - productData.price;

        productData.sale_category = this.isInSaleCategory(product);

        return productData;
    }

    protected isInSaleCategory(product: ProductDecorator<IProductDataV2>): boolean {
        return product.data?.category_ids?.includes(this.app.data?.['saleCategoryId']);
    }

    protected getProductCategoryIdFromMeta(): string | undefined {
        const currentModel = this.meta.model;
        const previousModel = this.meta.previousState?.model;
        if (
            currentModel &&
            currentModel.data &&
            currentModel.type === 'product' &&
            previousModel &&
            previousModel.data &&
            previousModel.type === 'category'
        ) {
            const product = currentModel as ProductDecorator<IProductDataV3>;
            const category = previousModel as Category;
            if (product.data.category_ids && product.data.category_ids.indexOf(category.id)) {
                return category.id.toString();
            }
        }
        return undefined;
    }

    protected override getCheckoutStepEventProperties(
        checkout: CheckoutFormComponent,
        stepBeforeName: string
    ): Exponea.ICheckoutStepChangeEventProperties | undefined {
        const superProperties = super.getCheckoutStepEventProperties(checkout, stepBeforeName);

        if (superProperties) {
            const stepBefore = checkout.getStepByName(stepBeforeName);

            if (stepBefore) {
                superProperties.step_number = findIndex(checkout.stepsSequence, item => item.name === stepBefore.name) + 1;
            }

            const paymentMethod = checkout.data.payment.method ? checkout.paymentMethodByCode(checkout.data.payment.method) : undefined;

            if (paymentMethod) {
                superProperties.payment_type = this.parsePaymentMethodTitle(paymentMethod.title, paymentMethod.code);
            }

            return {
                ...superProperties,
                total_price: Math.round((superProperties.total_price || 0) * this.getPriceGBPRate() * 100) / 100,
                total_price_without_tax: Math.round((superProperties.total_price || 0) * this.getPriceGBPRate() * 100) / 100,
                ...this.getLocalGeneralProperties(),
                ...this.getProductAndVariantLists()
            };
        }

        return superProperties;
    }

    protected getPriceGBPRate(): number {
        const currentCurrency = find(this.app.data.currency.list, currency => currency.code === 'GBP');
        if (currentCurrency) {
            return currentCurrency.rate;
        }
        return 1;
    }

    protected getLocalGeneralProperties(): IExponeaLocalGeneralData {
        const store = this.app.multisite.current;
        const langs = store?.lang?.filter(lang => lang !== 'x-default');
        return { local_currency: this.app.data.currency.base, language: langs?.[0].substring(0, 2), domain: store?.domain };
    }

    protected getProductAndVariantLists(): IExponeaProductAndVariantLists {
        const productList = map(this.basket.data.items, item => {
            const productId = item.product;
            if (productId) {
                const product = this.app.resources.product.getExisting(productId);
                if (product && product.parent && product.parent.data) {
                    const sku = product.parent.data.sku;
                    if (sku) {
                        return {
                            product_id: sku,
                            quantity: item.request.qty
                        };
                    }
                }
            }
            return undefined;
        });
        const productIds = map(productList, item => item && item.product_id);
        const variantList = map(this.basket.data.items, item => {
            const productId = item.product;
            if (productId) {
                const product = this.app.resources.product.getExisting(productId);
                if (product) {
                    const parent = product.parent;
                    if (product.data && parent.data && product.data.sku !== parent.data.sku) {
                        const sku = product.data.sku;
                        if (sku) {
                            return {
                                product_id: sku,
                                quantity: item.request.qty
                            };
                        }
                    }
                }
            }
            return undefined;
        }).filter(i => !!i);
        const variantIds = map(variantList, item => {
            if (item) {
                return item.product_id;
            }
            return undefined;
        });
        return { product_list: productList, product_ids: productIds, variant_list: variantList, variant_ids: variantIds };
    }

    /**
     * Returns view category event properties
     * @param {Category<ICategoryDataV3>} category
     * @returns {Exponea.IViewCategoryEventProperties}
     */
    protected override getViewCategoryEventProperties(category: Category<ICategoryDataV3>): Exponea.IViewCategoryEventProperties {
        const superProperties = super.getViewCategoryEventProperties(category);
        superProperties.listed_products = superProperties.category_listed_products;
        delete superProperties.category_listed_products;

        return { ...superProperties, ...this.getLocalGeneralProperties() };
    }

    protected override onPurchase(checkoutBeforeSubmitData: Exponea.ICheckoutSubmitData, successData: Checkout.ISuccessData) {
        const shippingAdditionalData = checkoutBeforeSubmitData.checkoutData.shipping.additionalData;

        const onLoad = () => super.onPurchase(checkoutBeforeSubmitData, successData);

        if (shippingAdditionalData && shippingAdditionalData.store_id) {
            this.app.resources.store.load(shippingAdditionalData.store_id, 1).finally(onLoad);
        } else {
            onLoad();
        }
    }

    override onNewsletterConsentChange(value: any, email: string, placement?: string, message?: string) {
        if (value !== undefined) {
            const currentStore = this.app.multisite.current;
            const action = this.getNewsletterAction(value);
            const eventProperties: Exponea.INewsletterConsent = {
                capture: 'Magento',
                store_id: currentStore?.id,
                action,
                category: 'newsletter',
                timestamp: Date.now(),
                identification_type: 'email',
                identification: email,
                source: 'public_api',
                placement: placement || 'public_api',
                valid_until: 'unlimited',
                message: message || '',
                current_website: currentStore?.name,
                ...this.getLocalGeneralProperties()
            };

            const date = this.datePipe.transform(new Date(), 'dd/MM/yy');

            if (value) {
                eventProperties.optin_date = date;
            } else {
                eventProperties.optout_date = date;
            }

            if (placement === 'footer_GDPR' || placement === 'subscription_module' || placement === 'backinstock') {
                this.identifyCustomer(email);
            }

            this.newsletterConsentEvent(eventProperties);
        }
    }

    protected override onRegister(): void {
        // reset core
        return;
    }

    protected onLoginOrRegisterRequest(newsletter: boolean, placement?: string) {
        this.updateCustomerData();
        this.identifyCustomer(this.app.customer.model.data?.email);
        this.onNewsletterConsentChange(newsletter, this.app.customer.model.data?.email as string, placement);
        // We do not follow usual subscription path, we delete newsletter property from login/register data
        // Because of it, core gtm service will not trigger newsletter event so we need to trigger it here.
        this.gtmService.trackNewsletter(newsletter, this.app.customer.model.data.email, placement);
    }

    protected override onCheckoutSuccessCallback(
        successData: Checkout.ISuccessData,
        checkoutBeforeSubmitData: Exponea.ICheckoutSubmitData
    ): void {
        const { checkoutData } = checkoutBeforeSubmitData;

        if (checkoutData.newsletter && !this.sessionStorageService.get('exponeaConsent')) {
            if (!this.sessionStorageService.get('expressPayment')) {
                this.onNewsletterConsentChange(checkoutData.newsletter, successData.customer.email, 'checkout_start_softoptin');
            }

            this.identifyCustomerOnCheckoutSuccess(successData);

            this.updateCustomerData(this.getCustomerDataFromCheckoutData(checkoutBeforeSubmitData, successData));
        }
    }

    protected override onAuthentication(): void {
        super.onAuthentication();
        this.timeoutService.setTimeout(() => this.identifyCustomer(), 20);
    }

    override identifyCustomer(
        identifier: number | string | undefined = this.app.customer.model.data?.email,
        id: number | string | undefined = this.app.customer.model.data?.id,
        watchdogEmail?: string,
        customProperties?: Record<string, unknown>
    ) {
        const idString = (id && id.toString()) || undefined;

        if (this.window.exponea && identifier) {
            const customerId: Exponea.ICustomerIdentify = { registered: identifier.toString(), magento_customer_id: idString };

            this.window.exponea.identify(customerId, customProperties);

            if (watchdogEmail) {
                // do not send the watchdog_email in identify() call!
                this.window.exponea.update({
                    watchdog_email: watchdogEmail
                });
            }
        }
    }

    protected override identifyCustomerOnCheckoutSuccess(successData: Checkout.ISuccessData) {
        this.identifyCustomer(successData.customer.email, successData.customer.id);
    }

    /**
     * Returns exponea customer update data from customer data
     * @returns {Exponea.ICustomerUpdate}
     */
    protected override getCustomerData(): Exponea.ICustomerUpdate {
        const superData = super.getCustomerData();
        superData.email = this.app.customer.model.data?.email;
        return superData;
    }

    protected override getCustomerDataFromCheckoutData(
        checkoutData: Exponea.ICheckoutSubmitData,
        successData: Checkout.ISuccessData
    ): Exponea.ICustomerUpdate {
        const superData = super.getCustomerDataFromCheckoutData(checkoutData, successData);
        superData.email = successData.customer.email;
        return superData;
    }

    checkConsent(status: Status | undefined, email: string, onSuccess?: IRequestCallback, onError?: IRequestCallback): void {
        this.request.send(
            status,
            {
                path: 'exponea/consent',
                data: { email }
            },
            onSuccess,
            onError
        );
    }

    override loadRecommendationsProductIds(options: string | Exponea.IRecommendationsOptions, productSku?: string[]): Promise<number[]> {
        return new Promise((resolve, reject) => {
            this.loadRecommendations(options, productSku)
                .then(async items => {
                    const skus = items?.length ? compact(items.map(item => item.item_id)) : [];

                    if (skus.length) {
                        try {
                            await this.app.resources.product.find({ verbosity: 2, filter: { sku: { $in: skus } } });

                            resolve(
                                this.app.resources.product
                                    .getAllExisting()
                                    .filter(i => skus.includes(i.data.sku))
                                    .map(i => i.id)
                            );
                        } catch (error) {
                            reject(error);
                        }
                    } else {
                        resolve([]);
                    }
                })
                .catch(reject);
        });
    }

    protected override getPurchaseEventProperties(
        checkoutSubmitData: Exponea.ICheckoutSubmitData,
        successData: Checkout.ISuccessData
    ): Exponea.IPurchaseEventProperties {
        const eventProperties = super.getPurchaseEventProperties(checkoutSubmitData, successData);

        eventProperties.payment_type = this.parsePaymentMethod(eventProperties.payment_type);

        return eventProperties;
    }

    protected override onCheckoutCompletedStep(data: Exponea.ICheckoutSubmitData): void {
        const { basketData, submissionData, checkoutData, checkoutBackendData } = data;
        const promoCodes = basketData.coupon;
        const paymentMethodCode = submissionData.paymentMethod.method;
        const paymentMethod = paymentMethodCode
            ? find(checkoutBackendData.payment.methods, method => method.code === paymentMethodCode)
            : undefined;
        const productList = this.getProductList(basketData);

        const eventProperties: Exponea.ICheckoutStepChangeEventProperties = {
            step_title: 'Completed',
            promo_code: isArray(promoCodes) ? promoCodes[0] : promoCodes,
            promo_total_value: basketData.coupon_value,
            payment_type: this.parsePaymentMethodTitle(paymentMethod?.['title'], paymentMethod?.['code']),
            delivery_type: checkoutData.shipping.method,
            delivery_cost: basketData.totals?.shipping,
            delivery_country: checkoutData.address.shipping.country || this.getCountryByCode(checkoutData.address.shipping.country_id),
            delivery_city: checkoutData.address.shipping.city,
            tax_value: basketData.totals && basketData.totals.tax,
            local_currency: this.app.data.currency.base,
            product_list: productList,
            product_ids: productList.map(item => item.product_id),
            total_quantity: basketData.qty,
            total_price: basketData.totals?.grand_total_global || basketData.totals?.grand_total,
            total_price_without_tax: this.getTotalWithoutTax(basketData),
            total_price_local_currency: basketData.totals && basketData.totals.grand_total
        };

        this.checkoutStepChangeEvent(eventProperties);
    }

    protected override parsePaymentMethodTitle(title?: string, code?: string | number): string {
        if (this.sessionStorageService.get('expressPayment') && this.expressPaymentMethods.indexOf(`${code}`) !== -1) {
            return `${title} Express`;
        }

        return title || '';
    }

    protected override parsePaymentMethod(code: string): string {
        if (this.sessionStorageService.get('expressPayment') && this.expressPaymentMethods.indexOf(`${code}`) !== -1) {
            return `${code}_express`;
        }

        return code;
    }

    override sendBackInStockEvent(variant_id: string): void {
        this.track({
            name: Exponea.Events.StockNotification,
            properties: { variant_id, store_id: this.app.multisite.current?.country } as Exponea.IStockNotificationEventProperties
        });
    }
}
